import { useCallback, useEffect, useRef, useState } from 'react';

import { Modal } from '@change/ds-core-ui';

import { useI18n } from '@change-corgi/core-react/i18n';

import type { DecisionMakerRecommenderModalOptions } from './DecisionMakerRecommenderModalContent';
import { DecisionMakerRecommenderModalContent } from './DecisionMakerRecommenderModalContent';

export type { DecisionMakerRecommenderModalOptions } from './DecisionMakerRecommenderModalContent';

export type OpenDecisionMakerRecommenderModal = (options: DecisionMakerRecommenderModalOptions) => Promise<void>;

/**
 * Imperative decision-maker recommender modal built on the Sunrise DS `Modal`.
 *
 * Returns a tuple `[openModal, modal]`:
 * - `openModal(options)` opens the modal and resolves once it is closed (close button, ESC, or
 *   outside click).
 * - `modal` is the React element that must be rendered in the consumer's tree.
 */
export function useDecisionMakerRecommenderModal(): [
	openModal: OpenDecisionMakerRecommenderModal,
	modal: React.ReactElement,
] {
	const { translate } = useI18n();

	const [isOpen, setIsOpen] = useState(false);
	const [options, setOptions] = useState<DecisionMakerRecommenderModalOptions | undefined>(undefined);

	const resolverRef = useRef<(() => void) | null>(null);

	const settle = useCallback(() => {
		setIsOpen(false);
		resolverRef.current?.();
		resolverRef.current = null;
	}, []);

	// Unlike the legacy global-portal modal, this controlled modal only settles via
	// `onClose`/`onOpenChange`. If the owning component unmounts while a request is pending
	// (e.g. the user clicks the "add a different decision maker" link and navigates away), resolve
	// the pending promise so the caller's close tracking / `onClose` callback still runs.
	useEffect(() => {
		return () => {
			resolverRef.current?.();
			resolverRef.current = null;
		};
	}, []);

	const openModal = useCallback<OpenDecisionMakerRecommenderModal>(async (opts) => {
		setOptions(opts);
		setIsOpen(true);

		// Resolve any prior pending promise so it doesn't hang if the modal is reopened
		// before the previous one was closed.
		resolverRef.current?.();

		// oxlint-disable-next-line promise/avoid-new
		return new Promise<void>((resolve) => {
			resolverRef.current = resolve;
		});
	}, []);

	const modal = (
		<Modal
			isOpen={isOpen}
			onClose={settle}
			onOpenChange={(open) => {
				if (!open) settle();
			}}
			closeButtonAriaLabel={translate('corgi.components.modal.close_button')}
		>
			{options && <DecisionMakerRecommenderModalContent {...options} />}
		</Modal>
	);

	return [openModal, modal];
}
